import React from "react";

function FieldCard({ field, fullWidth = false, testIdPrefix = "risk-print-field" }) {
  const isScore = field.type === "score";
  return (
    <div
      className={`risk-print-card${fullWidth ? " risk-print-sheet__full" : ""}${isScore ? " risk-print-card--score" : ""}`}
      data-testid={`${testIdPrefix}-${field.key}`}
      data-truncated={field.truncated ? "true" : "false"}
      data-band={field.band || undefined}
      data-band-color={field.bandColor || undefined}
      style={isScore && field.bandColor ? { background: field.bandColor, color: field.textColor } : undefined}
    >
      <div className="risk-print-card__label">{field.label}</div>
      <div className="risk-print-card__value">{field.value}</div>
      {isScore && field.band && field.band !== "-" && (
        <div className="risk-print-score-band">{field.band}</div>
      )}
      {field.meta && <div className="risk-print-card__meta">{field.meta}</div>}
    </div>
  );
}

function BadgeGroupCard({ field }) {
  return (
    <div className="risk-print-card risk-print-sheet__full risk-print-badge-group" data-testid={`risk-print-field-${field.key}`}>
      <div className="risk-print-card__label">{field.label}</div>
      <div className="risk-print-summary-buttons">
        {field.items.map((item) => (
          <span
            key={`${field.key}-${item.key}`}
            className="risk-print-summary-button"
            data-testid={`risk-print-summary-${field.key}-${item.key}`}
          >
            {item.count} {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ListCard({ field, testIdPrefix = "risk-print-field" }) {
  const hasItems = Array.isArray(field.items) && field.items.length > 0;

  return (
    <div
      className="risk-print-card risk-print-sheet__full"
      data-testid={`${testIdPrefix}-${field.key}`}
      data-truncated={field.truncated ? "true" : "false"}
    >
      <div className="risk-print-card__label">{field.label}</div>
      {hasItems ? (
        <ul className="risk-print-list">
          {field.items.map((item) => (
            <li key={`${field.key}-${item}`}>{item}</li>
          ))}
        </ul>
      ) : (
        <div className="risk-print-card__value">{field.emptyLabel || "-"}</div>
      )}
      {field.truncated && field.hiddenCount > 0 && (
        <div className="risk-print-card__meta">+ {field.hiddenCount} more not shown</div>
      )}
    </div>
  );
}

function MatrixCard({ field, testIdPrefix = "risk-print-field" }) {
  const headerAxes = Array.from({ length: field.matrixSize }, (_, index) => index + 1);
  return (
    <div className="risk-print-card risk-print-sheet__full risk-print-matrix-card" data-testid={`${testIdPrefix}-${field.key}`}>
      <div className="risk-print-card__label">{field.label}</div>
      <div
        className="risk-print-matrix"
        style={{ gridTemplateColumns: `28px repeat(${field.matrixSize}, minmax(0, 1fr))` }}
      >
        <div />
        {headerAxes.map((impact) => (
          <div key={`impact-${impact}`} className="risk-print-matrix__axis">
            I{impact}
          </div>
        ))}
        {Array.from({ length: field.matrixSize }, (_, rowIndex) => field.matrixSize - rowIndex).map((likelihood) => (
          <React.Fragment key={`row-${likelihood}`}>
            <div className="risk-print-matrix__axis">L{likelihood}</div>
            {field.cells
              .filter((cell) => cell.likelihood === likelihood)
              .map((cell) => (
                <div
                  key={cell.key}
                  className="risk-print-matrix__cell"
                  data-testid={`risk-print-matrix-cell-${cell.key}`}
                  style={{ background: cell.color, color: cell.textColor }}
                >
                  <div className="risk-print-matrix__score">{cell.score}</div>
                  <div className="risk-print-matrix__markers">
                    {cell.markers.map((marker) => (
                      <span key={`${cell.key}-${marker}`} className="risk-print-matrix__marker">
                        {marker}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
          </React.Fragment>
        ))}
      </div>
      {field.legend?.length > 0 && (
        <div className="risk-print-matrix__legend">
          {field.legend.map((item) => (
            <span key={item.label} className="risk-print-matrix__legend-item">
              <span className="risk-print-matrix__marker">{item.label}</span>
              <span>{item.text}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function TagCard({ field, testIdPrefix = "risk-print-field" }) {
  const hasItems = Array.isArray(field.items) && field.items.length > 0;

  return (
    <div
      className="risk-print-card risk-print-sheet__full"
      data-testid={`${testIdPrefix}-${field.key}`}
      data-truncated={field.truncated ? "true" : "false"}
    >
      <div className="risk-print-card__label">{field.label}</div>
      {hasItems ? (
        <div className="risk-print-tags">
          {field.items.map((item) => (
            <span key={`${field.key}-${item}`} className="risk-print-pill">
              {item}
            </span>
          ))}
        </div>
      ) : (
        <div className="risk-print-card__value">{field.emptyLabel || "-"}</div>
      )}
      {field.truncated && field.hiddenCount > 0 && (
        <div className="risk-print-card__meta">+ {field.hiddenCount} more not shown</div>
      )}
    </div>
  );
}

export default function RiskPrintPreview({
  preview,
  screen = false,
  root = false,
  testId = "risk-print-preview",
}) {
  const titleField = preview.sections.header.find((field) => field.key === "risk_name");
  const otherHeaderFields = preview.sections.header.filter((field) => field.key !== "risk_name");

  return (
    <div
      className={screen ? "risk-print-screen-preview" : undefined}
      data-risk-print-root={root ? "true" : undefined}
      data-testid={testId}
    >
      <article className="risk-print-sheet" data-print-layout="dedicated">
        <header className="risk-print-sheet__header">
          <h1 className="risk-print-sheet__title">{titleField?.value || "Risk overview"}</h1>
          {otherHeaderFields.length > 0 && (
            <div className="risk-print-sheet__header-meta">
              {otherHeaderFields.map((field) => (
                <span key={field.key} className="risk-print-pill">
                  {field.label}: {field.value}
                </span>
              ))}
            </div>
          )}
        </header>

        {preview.sections.summary.length > 0 && (
          <section className="risk-print-sheet__summary">
            {preview.sections.summary.map((field) => (
              <FieldCard key={field.key} field={field} />
            ))}
          </section>
        )}

        {preview.sections.matrix.length > 0 && (
          <section className="risk-print-sheet__detail">
            {preview.sections.matrix.map((field) => (
              <MatrixCard key={field.key} field={field} />
            ))}
          </section>
        )}

        {preview.sections.detail.length > 0 && (
          <section className="risk-print-sheet__detail">
            {preview.sections.detail.map((field) => (
              <FieldCard
                key={field.key}
                field={field}
                fullWidth={field.key === "description" || field.key === "latest_note"}
              />
            ))}
          </section>
        )}

        {preview.sections.badges.length > 0 && (
          <section className="risk-print-sheet__detail">
            {preview.sections.badges.map((field) => (
              <BadgeGroupCard key={field.key} field={field} />
            ))}
          </section>
        )}

        {preview.sections.list.length > 0 && (
          <section className="risk-print-sheet__detail">
            {preview.sections.list.map((field) => (
              <ListCard key={field.key} field={field} />
            ))}
          </section>
        )}

        {preview.sections.tag.length > 0 && (
          <section className="risk-print-sheet__detail">
            {preview.sections.tag.map((field) => (
              <TagCard key={field.key} field={field} />
            ))}
          </section>
        )}
      </article>
    </div>
  );
}
